import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

function useIntersection(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const TECH_ICONS = [
  "ri-map-pin-2-line",
  "ri-translate-2",
  "ri-shield-check-line",
  "ri-star-line",
];
const TECH_KEYS = ["tech1", "tech2", "tech3", "tech4"] as const;

export default function ServicesSection() {
  const { t } = useTranslation();
  const { ref, visible } = useIntersection();

  return (
    <section
      id="services"
      className="py-24 md:py-32 px-4"
      style={{ backgroundColor: "oklch(0.10 0.001 10)" }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <p className="text-xs font-label tracking-widest uppercase mb-4" style={{ color: "oklch(0.74 0.18 55)" }}>
            {t("services.label")}
          </p>
          <h2 className="font-heading font-black text-3xl md:text-5xl mb-4 leading-tight" style={{ color: "white" }}>
            {t("services.title")}
          </h2>
          <p className="font-label text-sm md:text-base" style={{ color: "rgba(255,255,255,0.45)" }}>
            {t("services.desc")}
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TECH_KEYS.map((key, i) => (
            <div
              key={key}
              className={`group rounded-xl p-6 border cursor-default transition-all duration-500 overflow-hidden relative
                ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{
                backgroundColor: "oklch(0.15 0.001 10)",
                borderColor: "rgba(255,255,255,0.06)",
                transitionDelay: `${i * 80}ms`,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(245,158,11,0.40)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(255,255,255,0.06)";
              }}
            >
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{ background: "radial-gradient(circle at 20% 20%, rgba(245,158,11,0.07) 0%, transparent 65%)" }}
              />

              {/* Tag */}
              <span
                className="inline-block px-2.5 py-0.5 rounded-full text-xs font-label font-semibold mb-4"
                style={{ backgroundColor: "rgba(245,158,11,0.12)", color: "oklch(0.74 0.18 55)" }}
              >
                {t(`services.${key}_tag`)}
              </span>

              {/* Icon */}
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-colors duration-300 group-hover:bg-primary-500"
                style={{ backgroundColor: "rgba(245,158,11,0.10)" }}
              >
                <i
                  className={`${TECH_ICONS[i]} text-xl transition-colors duration-300 group-hover:text-foreground-950`}
                  style={{ color: "oklch(0.74 0.18 55)" }}
                ></i>
              </div>

              <h3 className="font-heading font-bold text-base mb-3 leading-snug" style={{ color: "rgba(255,255,255,0.9)" }}>
                {t(`services.${key}_title`)}
              </h3>
              <p className="text-xs font-label leading-relaxed" style={{ color: "rgba(255,255,255,0.45)" }}>
                {t(`services.${key}_desc`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}